/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
'use strict';
import { Collection, MapBrowserEvent } from 'ol';
import Feature from 'ol/Feature';
import { Pixel } from 'ol/Pixel';
import { Coordinate } from 'ol/coordinate';
import { Color } from 'ol/color';
import { Extent, getSize } from 'ol/extent';
import { Polygon } from 'ol/geom';
import { defaults, DragBox, DragPan, Draw, Interaction, Pointer } from 'ol/interaction';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { Vector as VectorSource } from 'ol/source';

import { MapQuerySpatialFilter } from '../../../types';
import { LoggerService } from '../../shared/services/logger.service';
import { DeviceService } from '../../shared/services/device.service';
import { MapService } from './map.service';
import { MeasureService } from '../../shared/services/measure.service';
import { QueryService } from './query.service';
import { Injectable, NgZone } from '@angular/core';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { CopyCoordinates, MapCreated, UserSelection } from '../state/active-map.actions';
import { ColorLike } from 'ol/colorlike';
import { toLonLat } from 'ol/proj';
import { Measure, MeasureOptions } from '../interaction/measure.interaction';

@Injectable()
export class MapInteractionsService {
    private _interactionStore: Map<string, Interaction>;
    private markupUndoStack: Feature[];
    private markupColor: Color;
    private markupText: string;
    private markupDrawEndListener: (Event) => any;
    private measureColor: ColorLike;
    private polygonSelectColor: ColorLike;

    /** @member clickTolerance {number} The click tolerance to set in the Draw Interaction */
    private readonly clickTolerance: number;

    /**
     *
     * @param {LoggerService} logger
     * @param {MapService} mapService
     * @param {QueryService} queryService
     * @param {MeasureService} measureService
     * @param {DeviceService} deviceService
     * @param {NgZone} ngZone
     * @param {Actions} actions$
     * @param {Store} store
     */
    constructor(
        private logger: LoggerService,
        private mapService: MapService,
        private queryService: QueryService,
        private measureService: MeasureService,
        private deviceService: DeviceService,
        private ngZone: NgZone,
        private actions$: Actions,
        private store: Store
    ) {
        this._interactionStore = new Map();
        this.markupUndoStack = [];

        // on map reload, clear all interaction and reset to default
        this.actions$.pipe(ofActionSuccessful(MapCreated)).subscribe(() => {
            this.removeInteractions();
            this._interactionStore.clear();
            this.enablePanInteraction();
        });

        // set clickTolerance based on touch capability
        this.clickTolerance = this.deviceService.hasTouchEvents ? 6 : 2;
    }

    public pauseInteractions() {
        this.mapService.map.getInteractions().forEach((i: Interaction) => {
            i.setActive(false);
        });
    }

    public resumeInteractions() {
        this.mapService.map.getInteractions().forEach((i: Interaction) => {
            i.setActive(true);
        });
    }

    /**
     * Return an ol.Collection of the default interactions
     */
    public getDefaultInteractions(): Collection<Interaction> {
        return this.ngZone.runOutsideAngular(() =>
            defaults({
                altShiftDragRotate: false,
                dragPan: false,
                pinchRotate: false,
                zoomDuration: 0,
            }).extend([this.getDragPanInteraction(), this.getSelectPointInteraction()])
        );
    }

    public enablePanInteraction() {
        this.enableDefaultInteractions();
        this.mapService.setCursor('move');
    }

    public enableSelectBoxInteraction() {
        // minimum select box size
        const pixelBuffer = 2;
        // remove any current interactions
        this.removeInteractions();
        let selectInteraction;
        if (this._interactionStore.has('select-box')) {
            selectInteraction = this._interactionStore.get('select-box');
        } else {
            selectInteraction = new DragBox({
                // allow select on click
                boxEndCondition: () => true,
            });
            selectInteraction.on('boxend', (e) => {
                let boundingBox = selectInteraction.getGeometry().getExtent();
                const modifiers = MapInteractionsService.getModifierKeys(e);
                const size = getSize(boundingBox);
                if (0 === size[0] || 0 === size[1]) {
                    const ul = this.mapService.map.getPixelFromCoordinate([boundingBox[0], boundingBox[1]]);
                    const br = this.mapService.map.getPixelFromCoordinate([boundingBox[2], boundingBox[3]]);
                    ul[0] -= pixelBuffer;
                    ul[1] -= pixelBuffer;
                    br[0] += pixelBuffer;
                    br[1] += pixelBuffer;
                    const coordinatesUL = this.mapService.map.getCoordinateFromPixel(ul);
                    const coordinatesBR = this.mapService.map.getCoordinateFromPixel(br);
                    boundingBox = [coordinatesBR[0], coordinatesBR[1], coordinatesUL[0], coordinatesUL[1]];
                }

                const filter: MapQuerySpatialFilter = {
                    filter: { type: 'bbox', bbox: boundingBox },
                    type: 'spatial',
                };
                this.store.dispatch(new UserSelection());
                this.queryService.doQuery(filter, modifiers.shiftKey).subscribe();
            });
            this._interactionStore.set('select-box', selectInteraction);
        }
        const map = this.mapService.map;
        map.addInteraction(selectInteraction);
        this.mapService.setCursor('default');
    }

    public enableZoomInInteraction() {
        if (this.deviceService.hasTouchEvents) {
            this.enablePanInteraction();
        } else {
            this.enableMouseZoomInInteraction();
        }
    }

    public enableZoomOutInteraction() {
        if (this.deviceService.hasTouchEvents) {
            this.enablePanInteraction();
        } else {
            this.enableMouseZoomOutInteraction();
        }
    }

    public enableMouseZoomInInteraction() {
        const map = this.mapService.map;

        // remove any current interactions
        this.removeInteractions();
        let zoomInInteraction;
        if (this._interactionStore.has('mouseZoomIn')) {
            zoomInInteraction = this._interactionStore.get('mouseZoomIn');
        } else {
            zoomInInteraction = new DragBox({
                // allow select on click
                boxEndCondition: () => true,
            });
            zoomInInteraction.on('boxend', () => {
                const mapObj = this.mapService.map;
                const boundingBox = zoomInInteraction.getGeometry().getExtent();
                mapObj.getView().fit(boundingBox, { size: mapObj.getSize() });
            });
            this._interactionStore.set('mouseZoomIn', zoomInInteraction);
        }
        map.addInteraction(zoomInInteraction);
        this.mapService.setCursor('zoom-in');
    }

    public enableMouseZoomOutInteraction() {
        // remove any current interactions
        this.removeInteractions();
        let zoomOutInteraction;
        if (this._interactionStore.has('zoomOut')) {
            zoomOutInteraction = this._interactionStore.get('zoomOut');
        } else {
            zoomOutInteraction = new Pointer({
                handleEvent: (event) => {
                    if ('click' === event.type) {
                        const view = event.map.getView();
                        view.setCenter(event.coordinate);
                        const zoom = view.getZoom();
                        view.setZoom(zoom - 1);
                    }
                    return true;
                },
            });
            this._interactionStore.set('zoomOut', zoomOutInteraction);
        }
        const map = this.mapService.map;
        map.addInteraction(zoomOutInteraction);
        this.mapService.setCursor('zoom-out');
    }

    // public enableTouchZoomInteraction() {
    //     // remove any current interactions
    //     this.removeInteractions();
    //     let touchZoomInteraction;
    //     if (this._interactionStore.has("touchZoom")) {
    //         touchZoomInteraction = this._interactionStore.get("touchZoom");
    //     } else {
    //         // because google maps needs a whole number for the zoom value,
    //         // constrain the zoom
    //         touchZoomInteraction = new ol.interaction.PinchZoom({constrainResolution: true});
    //         this._interactionStore.set("touchZoom", touchZoomInteraction);
    //     }
    //     const map = this.mapService.map;
    //     map.addInteraction(touchZoomInteraction);
    // }

    /**
     * Turns on digitizing for a single point
     * @param fn A function to post-process the point, expects to be bound to
     * context/this
     * @param layer {string}
     * @param key {any}
     */
    public enableDigitizePointInteraction(fn, layer, key?) {
        // remove any current interactions
        this.removeInteractions();
        const digitizeInteraction = new Draw({
            clickTolerance: 4,
            maxPoints: 1,
            style: new Style({
                image: new Circle({
                    fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
                    radius: 0,
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 0)',
                        width: 0,
                    }),
                }),
            }),
            type: 'Point',
        });
        const drawEndFn = (digitizeLayer, digitizeKey, drawEvent) => {
            /** @var arg {DrawEvent} */
            const coordinates = drawEvent.feature.getGeometry().getCoordinates();
            if (digitizeKey) {
                fn(digitizeLayer, digitizeKey, coordinates);
            } else {
                fn(digitizeLayer, coordinates);
            }
            this.removeInteractions();
        };
        digitizeInteraction.once('drawend', drawEndFn.bind(this, layer, key));
        const map = this.mapService.map;
        map.addInteraction(digitizeInteraction);
        this.mapService.setCursor('crosshair');
    }

    public enableMarkup(markupType, color, text) {
        this.markupColor = color;
        if (text) {
            this.markupText = text;
        }
        let markupInteraction = null;
        this.removeInteractions();
        const markupLayer = this.mapService.getVectorLayer('markup');
        let markupStyle;

        switch (markupType) {
            case 'line':
                markupStyle = () =>
                    new Style({
                        fill: null,
                        stroke: new Stroke({
                            color: this.markupColor,
                            width: 2,
                        }),
                    });
                if (this._interactionStore.has('markup_line')) {
                    markupInteraction = this._interactionStore.get('markup_line');
                    markupInteraction.un('drawend', this.markupDrawEndListener);
                } else {
                    markupInteraction = new Draw({
                        clickTolerance: 4,
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'LineString',
                    });
                }
                this.markupDrawEndListener = this.getMarkupDrawEndListener(markupStyle);
                markupInteraction.on('drawend', this.markupDrawEndListener);
                this._interactionStore.set('markup_line', markupInteraction);
                break;
            case 'rect':
                markupStyle = () =>
                    new Style({
                        fill: null,
                        stroke: new Stroke({
                            color: this.markupColor,
                            width: 2,
                        }),
                    });
                if (this._interactionStore.has('markup_rect')) {
                    markupInteraction = this._interactionStore.get('markup_rect');
                    markupInteraction.un('drawend', this.markupDrawEndListener);
                } else {
                    markupInteraction = new Draw({
                        geometryFunction: (coordinates: Coordinate[], geometry) => {
                            const start = coordinates[0];
                            const end = coordinates[1];
                            const polyCoords = [[start, [start[0], end[1]], end, [end[0], start[1]], start]];
                            if (!geometry) {
                                geometry = new Polygon(polyCoords);
                            } else {
                                (geometry as Polygon).setCoordinates([
                                    [start, [start[0], end[1]], end, [end[0], start[1]], start],
                                ]);
                            }
                            return geometry;
                        },
                        maxPoints: 2,
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'LineString',
                    });
                }
                this.markupDrawEndListener = this.getMarkupDrawEndListener(markupStyle);
                markupInteraction.on('drawend', this.markupDrawEndListener);
                this._interactionStore.set('markup_rect', markupInteraction);
                break;
            case 'circle':
                markupStyle = () =>
                    new Style({
                        fill: null,
                        stroke: new Stroke({
                            color: this.markupColor,
                            width: 2,
                        }),
                    });
                if (this._interactionStore.has('markup_circle')) {
                    markupInteraction = this._interactionStore.get('markup_circle');
                    markupInteraction.un('drawend', this.markupDrawEndListener);
                } else {
                    markupInteraction = new Draw({
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'Circle',
                    });
                }
                this.markupDrawEndListener = this.getMarkupDrawEndListener(markupStyle);
                markupInteraction.on('drawend', this.markupDrawEndListener);
                this._interactionStore.set('markup_circle', markupInteraction);
                break;
            case 'polygon':
                markupStyle = () =>
                    new Style({
                        fill: null,
                        stroke: new Stroke({
                            color: this.markupColor,
                            width: 2,
                        }),
                    });
                if (this._interactionStore.has('markup_polygon')) {
                    markupInteraction = this._interactionStore.get('markup_polygon');
                    markupInteraction.un('drawend', this.markupDrawEndListener);
                } else {
                    markupInteraction = new Draw({
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'Polygon',
                    });
                }
                this.markupDrawEndListener = this.getMarkupDrawEndListener(markupStyle);
                markupInteraction.on('drawend', this.markupDrawEndListener);
                this._interactionStore.set('markup_polygon', markupInteraction);
                break;
            case 'text':
                markupStyle = () =>
                    new Style({
                        text: new Text({
                            fill: new Fill({ color: this.markupColor }),
                            font: 'condensed 14pt Roboto',
                            text: this.markupText,
                        }),
                    });
                if (this._interactionStore.has('markup_text')) {
                    markupInteraction = this._interactionStore.get('markup_text');
                    markupInteraction.un('drawend', this.markupDrawEndListener);
                } else {
                    markupInteraction = new Draw({
                        source: markupLayer.getSource() as VectorSource,
                        style: new Style({
                            fill: null,
                            stroke: null,
                        }),
                        type: 'Point',
                    });
                }
                this.markupDrawEndListener = this.getMarkupDrawEndListener(markupStyle);
                markupInteraction.on('drawend', this.markupDrawEndListener);
                this._interactionStore.set('markup_text', markupInteraction);
                break;
            default:
                break;
        }
        const map = this.mapService.map;
        map.addInteraction(markupInteraction);
        this.mapService.setCursor('crosshair');
    }

    public undoMarkup() {
        const feature = this.markupUndoStack.pop();
        this.removeMarkupFeature(feature);
    }

    public clearMarkup() {
        const layer = this.mapService.getVectorLayer('markup');
        if (layer) {
            layer.getSource().clear(true);
        }
    }

    public removeMarkupFeature(feature) {
        const layer = this.mapService.getVectorLayer('markup');
        if (layer) {
            layer.getSource().removeFeature(feature);
        }
    }

    public enableMeasure(options: any) {
        let measureInteraction: any;
        this.removeInteractions();
        this.measureService.setMap(this.mapService.map);

        if (this._interactionStore.has('measure_line')) {
            measureInteraction = this._interactionStore.get('measure_line') as Measure;
        } else {
            const measureOptions: MeasureOptions = {
                clickTolerance: this.clickTolerance,
            };
            if (options['displayUnits']) {
                measureOptions.displayUnits = options['displayUnits'];
            }
            measureInteraction = new Measure(measureOptions);
            measureInteraction.on('measurestart', options.onStart ?? function () {});
            measureInteraction.on('measurechange', options.onChange ?? function () {});
            measureInteraction.on('measureend', options.onEnd ?? function () {});
        }
        this._interactionStore.set('measure_line', measureInteraction);

        this.mapService.map.addInteraction(measureInteraction);
        this.mapService.setCursor('crosshair');
    }

    public clearMeasure(): void {
        if (this._interactionStore.has('measure_line')) {
            const measureInteraction = this._interactionStore.get('measure_line') as Measure;
            measureInteraction.clearMeasure();
        }
    }

    public enablePolygonSelect(selectFn) {
        this.polygonSelectColor = '#838383';
        let polygonSelectInteraction;
        this.removeInteractions();
        const polygonSelectLayer = this.mapService.createPolygonSelectLayer();
        const polygonSelectStyle = new Style({
            fill: null,
            stroke: new Stroke({
                color: this.polygonSelectColor,
                width: 1,
            }),
        });

        if (this._interactionStore.has('polygon_select')) {
            polygonSelectInteraction = this._interactionStore.get('polygon_select');
        } else {
            polygonSelectInteraction = new Draw({
                clickTolerance: 4,
                source: polygonSelectLayer.getSource(),
                style: polygonSelectStyle,
                type: 'Polygon',
            });
        }
        polygonSelectInteraction.once('drawend', selectFn, this);
        this._interactionStore.set('polygon_select', polygonSelectInteraction);
        this.mapService.map.addInteraction(polygonSelectInteraction);
        this.mapService.setCursor('crosshair');
    }

    public clearPolygonSelect() {
        const layer = this.mapService.getPolygonSelectLayer();
        if (layer) {
            layer.getSource().clear(true);
        }
        const polygonSelectInteraction = this._interactionStore.get('polygon_select');
        this.mapService.map.removeInteraction(polygonSelectInteraction);
    }

    public enableRedline(redlineType, interactionOptions, drawEndFn) {
        if (this._interactionStore.has('redline')) {
            this._interactionStore.delete('redline');
        }
        this.removeInteractions();
        let redlineInteraction = null;
        switch (redlineType) {
            case 'line':
            case 'point':
                redlineInteraction = new Draw(interactionOptions);
                redlineInteraction.on('drawend', drawEndFn);
                break;
            default:
                break;
        }
        this._interactionStore.set('redline', redlineInteraction);
        this.mapService.map.addInteraction(redlineInteraction);
        this.mapService.setCursor('crosshair');
    }

    public enableClickToCopyCoordinates() {
        let copyInteraction;
        this.removeInteractions();
        if (this._interactionStore.has('copy-coordinates')) {
            copyInteraction = this._interactionStore.get('copy-coordinates');
        } else {
            const handler = (e: MapBrowserEvent<MouseEvent>) => {
                if ('click' === e.type) {
                    const userCoords = this.mapService.map.getCoordinateFromPixel(e.pixel);
                    const globalCoords = toLonLat(userCoords);
                    this.store.dispatch(new CopyCoordinates(globalCoords));
                    this.enablePanInteraction();
                    return false;
                }
                return true;
            };
            copyInteraction = new Pointer({
                // create pixel buffer around touch point
                handleDownEvent: () => false,
                handleEvent: handler,
                handleUpEvent: () => false,
            });
        }
        this._interactionStore.set('copy-coordinates', copyInteraction);
        this.mapService.map.addInteraction(copyInteraction);
        this.mapService.setCursor('crosshair');
    }

    /**
     * Removes all interactions added by the mapInteractions service. Does not affect
     * default interactions (unless overridden by the service.)
     */
    public removeInteractions() {
        this._interactionStore.forEach((interaction) => {
            this.mapService.map.removeInteraction(interaction);
        });
        this.mapService.setCursor('move');
    }

    private static getModifierKeys(e) {
        const event = e.mapBrowserEvent ? e.mapBrowserEvent : e;
        return {
            altKey: event.originalEvent.altKey,
            ctrlKey: event.originalEvent.ctrlKey,
            shiftKey: event.originalEvent.shiftKey,
        };
    }

    private enableDefaultInteractions() {
        this.mapService.map.getInteractions().clear();
        this.getDefaultInteractions().forEach((item) => {
            this.mapService.map.addInteraction(item);
        });
    }

    private getDragPanInteraction(): DragPan {
        let dragPan;
        if (this._interactionStore.has('drag-pan')) {
            dragPan = this._interactionStore.get('drag-pan');
        } else {
            dragPan = new DragPan({ kinetic: null });
            this._interactionStore.set('drag-pan', dragPan);
        }

        return dragPan;
    }
    /**
     * Returns an interaction that can select a point by a click (using the selectTolerance setting)
     * or by dragging a window.
     * @private
     * @return
     */
    private getSelectPointInteraction(): Interaction {
        let selectInteraction: Interaction;
        if (this._interactionStore.has('select-point')) {
            selectInteraction = this._interactionStore.get('select-point');
        } else {
            const pixelBuffer = this.selectTolerance;
            const handler = (e: MapBrowserEvent<MouseEvent>) => {
                if ('click' === e.type) {
                    const modifiers = MapInteractionsService.getModifierKeys(e);
                    const ul: Pixel = [e.pixel[0] - pixelBuffer, e.pixel[1] - pixelBuffer];
                    const br: Pixel = [e.pixel[0] + pixelBuffer, e.pixel[1] + pixelBuffer];
                    const coordinatesUL = this.mapService.map.getCoordinateFromPixel(ul);
                    const coordinatesBR = this.mapService.map.getCoordinateFromPixel(br);
                    const boundingBox: Extent = [
                        Math.min(coordinatesBR[0], coordinatesUL[0]),
                        Math.min(coordinatesBR[1], coordinatesUL[1]),
                        Math.max(coordinatesBR[0], coordinatesUL[0]),
                        Math.max(coordinatesBR[1], coordinatesUL[1]),
                    ];

                    this.store.dispatch(new UserSelection());
                    const filter: MapQuerySpatialFilter = {
                        filter: { type: 'bbox', bbox: boundingBox },
                        type: 'spatial',
                    };
                    this.queryService.doQuery(filter, modifiers.shiftKey).subscribe();
                    return false;
                }
                return true;
            };
            selectInteraction = new Pointer({
                // create pixel buffer around touch point
                handleDownEvent: () => false,
                handleEvent: handler,
                handleUpEvent: () => false,
            });
            this._interactionStore.set('select-point', selectInteraction);
        }

        return selectInteraction;
    }

    private getMarkupDrawEndListener(markupStyleBuilder) {
        return (drawEvent) => {
            this.markupUndoStack.push(drawEvent.feature);
            drawEvent.feature.setStyle(markupStyleBuilder());
        };
    }

    get hasMarkupFeatures() {
        return 0 < this.markupUndoStack.length;
    }

    get selectTolerance() {
        return this.store.selectSnapshot(AppConfigState.selectTolerance);
    }
}
